import type { LensCardType } from '~/types/leLens';

import type { TierDataset, TierProposal } from './lensTier';

/**
 * What a Lens card can actually be. Kept in one place because every prompt
 * needs the same list: when the renderer gains or loses something, the
 * generators must not keep offering the old shape.
 */
const CAPABILITIES = `LE Lens can build exactly these cards, and nothing else:
- "metric": one number. The query returns a single row with a single numeric column.
- "line": a trend over time. One label or date column, then one or more numeric columns.
- "bar": a comparison across categories. One label column, then one or more numeric columns.
- "drivers": ranked positive and negative change by category against the Lens comparison period. One category column and one numeric measure; Lens calculates absolute and percentage change from the two periods.
- "stacked": a total split by category. Exactly three columns in this order — a label or date column, a category column to stack by, and one numeric value column.
- "area": a total over time, filled. The same columns as a line; add a category column to show how the total divides between its parts.
- "scatter": one point per row, plotting two measures against each other. Two numeric columns, optionally a category column to colour by. Do not aggregate: the individual rows are the point.
- "pie": a share of a whole at one moment. One category column and one measure. Only where the parts sum to a meaningful total, and only for a handful of categories.
- "combo": measures of different units together, one on each of two scales. A label or date column and two or more numeric columns.
- "heatmap": a value across two categorical axes, such as day of week by hour of day, or market by month. Three columns in this order: a category for the x axis, a category for the y axis, and one numeric value for the cell.
- "boxplot": the spread of a value within each category, not just its total. A category column and a numeric value column, one row per observation, since the distribution across rows is the point.
- "table": detail. Clear named columns.
- "insight": a written answer rather than a picture. It carries a question, not a query, and is answered in prose each time the dashboard is read. Use it for something a chart cannot say, and at most once or twice: each one costs a call every time the dashboard opens.

Limits you must design within:
- Every chart except "combo" has ONE y axis, so its series must share a unit. A percentage beside a count on one scale flattens the smaller series against the bottom and the chart cannot be read.
- Where measures of different units belong together, use "combo" and say which series sits on the second scale. Prefer two separate cards: two scales let a reader see a relationship that the numbers do not support, so use "combo" only when the comparison is the point.
- Never mix units on any other chart type.
- A chart shows at most 8 series.

Presentation is set per card, not in SQL:
- Value format: "number", "currency" with a 3-letter code, or "percent". If the SQL already multiplies by 100, the card records that; do not divide in SQL to compensate.
- Sizes: "small" (a metric), "medium", "large" (a chart), "full" (a wide chart, an area or a table). Rows are balanced automatically, so pick the size that suits the content.
- Sorting, series order, colours and date formats are card settings. Do not encode them in SQL beyond a sensible ORDER BY.`;

/**
 * The choice that decides what a request costs.
 *
 * Stated as an ordered checklist because the order is the whole rule: a rate
 * asked for as a query becomes a stored rate column that no coarser card can
 * roll up, and a chart-type change asked for as a query puts a second version
 * of the same number on the board. Working down the list makes the cheapest
 * honest answer the first one reached, and stating the tier in the reply is
 * what lets the application hold the answer to it.
 */
export const TIER_DECISION = `Before writing anything, decide which of three tiers answers this, in this order, and take the first that honestly works:

1. VIEW CONFIGURATION ONLY. Existing data and existing fields already answer it and nothing has to be recomputed: a different chart type, a different split, a sort, a row limit, an axis bound, a number format, or a coarser period. Change what the card shows.
2. A CALCULATED FIELD over columns a dataset already returns. Every rate, share, average, per-unit value, difference and conditional subtotal is this tier, because each is arithmetic over columns that are already there. Use "on totals" (when: aggregate) for anything with a denominator, so the number is a ratio of sums and stays correct at every grain. Use "on each row" (when: row) only for a pre-grouping column, such as a subtotal under a condition. A rate is never a stored column: a stored rate cannot be rolled up.
3. A WAREHOUSE QUERY, and only when the data is not in any dataset the board already has: a different grain, a different table, a different population or a column nothing returns.

State the tier you chose in your reply, on its own two lines, before anything else:

  -- tier: view|field|query
  -- tierReason: one sentence saying why the tiers above it do not answer this

A reply that reaches for tier 3 while tier 1 or tier 2 answers the request is rejected without being run.`;

/**
 * Tells the model what the board already has, in the same terms the guard used
 * to work out which tier can answer the request.
 *
 * Priming with the columns rather than only with the dataset names is what
 * makes tier 1 and tier 2 reachable: a model that cannot see `ttv_aud` and
 * `gross_margin_aud` has no way to know a margin rate needs no query.
 */
export function tierPrimer(datasets: ReadonlyArray<TierDataset>, proposal?: TierProposal): string {
  if (!datasets.length) {
    return 'This board has no shared dataset yet, so a query is the only tier available.';
  }

  const listed = datasets.map((dataset) => `  ${dataset.id}: ${[...dataset.columns].join(', ')}`).join('\n');

  const guidance = proposal
    ? `\n\nThese columns already exist, so tier ${proposal.tier === 'view' ? '1' : proposal.tier === 'field' ? '2' : '3'} is expected here: ${proposal.reason}`
    : '';

  return `These columns already exist on this board:\n\n${listed}${guidance}`;
}

/**
 * Dashboard filters are applied by wrapping the saved query and matching a
 * selection against its output columns by name, so a card never references a
 * filter itself. It only has to expose the columns those filters already use.
 */
function filterRule(columns: Array<string>): string {
  return `This Lens has filters bound to these columns: ${columns.join(', ')}. Project each of them in the result, named exactly as given, so a filter selection can be matched against it. Do not write a token or placeholder for a filter: the Lens applies one by wrapping this query and filtering on its output columns directly.`;
}

/**
 * A table the model has already looked up, passed forward so the next writer
 * does not look it up again.
 *
 * Discovery is the same work every time, and every repeat of it is a tool call
 * and another turn while the reader waits: the turn that had to do it declares
 * what it found, and the turns after it are given the answer.
 */
/**
 * A measure someone has already certified, carried with the table it reads.
 *
 * The expression is the whole point: two cards that word the same measure
 * differently disagree on the dashboard, and a certified definition is the
 * only thing that stops the turns writing them from each deciding afresh.
 */
export type VerifiedMetric = { id: string; expression: string; avoid?: Array<string> };

export type VerifiedSource = {
  table: string;
  date?: string;
  columns: Array<string>;
  metrics?: Array<VerifiedMetric>;
};

function metricLines(metrics: ReadonlyArray<VerifiedMetric>): string {
  return metrics
    .map(
      (metric) =>
        `\n    certified ${metric.id}: ${metric.expression}${
          metric.avoid?.length ? ` — never filter on ${metric.avoid.join(' or ')}` : ''
        }`,
    )
    .join('');
}

function schemaRule(sources: ReadonlyArray<VerifiedSource>): string {
  const listed = sources
    .map(
      (source) =>
        `  ${source.table}${source.date ? `\n    period column: ${source.date}` : ''}\n    columns: ${source.columns.join(', ')}${source.metrics?.length ? metricLines(source.metrics) : ''}`,
    )
    .join('\n');
  const certified = sources.some((source) => source.metrics?.length)
    ? ' Where a measure above carries a certified definition, write that expression exactly and apply none of the filters it rules out: a certified measure worded a second way is wrong even when it runs.'
    : '';

  return `These tables have already been looked up for this dashboard, and they are the ones to write against:

${listed}

Write the query straight from that listing.${certified} Do not call the schema tools to confirm it and do not run the query: both have been done already, and every tool call is time the reader spends waiting on a spinner. Reach for a tool only if what this request needs is genuinely not listed above.`;
}

export function planPrompt(brief: string, correction?: string): string {
  return `You are designing an LE Lens analytics dashboard for this brief:

${brief}

${CAPABILITIES}

Plan the dashboard. Do not write SQL yet. Reply with exactly one fenced json block and nothing else after it, in this shape:

{"sections":[{"title":"Section name","cards":[{"title":"Card name","type":"metric|line|bar|stacked|area|scatter|pie|combo|heatmap|boxplot|table|insight","size":"small|medium|large|full","request":"a single self-contained question for one card","format":{"kind":"number|currency|percent","currency":"AUD","decimals":0,"percentScale":"percent"}}]}]}

Guidance:
- Group cards into 2 to 4 sections that each answer a different cut of the brief.
- Open with a section of "metric" cards at "small" size for the headline numbers.
- Prefer 6 to 10 cards in total.
- Use "scatter" only where two measures genuinely describe the same rows, such as discount against margin. It is not a substitute for a trend.
- Use "pie" sparingly: a share is usually read better as a bar. Never for more than a handful of categories, and never for something that changes over time.
- For a "combo", the card works out which series needs the second scale from the data. Do not try to set it: the split and the mark per series belong to whoever edits the card, and every config key except "format" is dropped before a card you produce is saved.
- Use the approved Luxury Escapes schema tools to confirm the data exists before planning a card. Omit any card you cannot confirm: a missing card is better than one that cannot be answered.
- Set "format" only when you are certain of the unit. A percentage already multiplied by 100 in SQL takes "percentScale":"percent"; a 0 to 1 ratio takes "unit".
- Keep each "title" to at most six words, and do not restate the brief in it.
- Each "request" must stand alone: it is sent on its own to generate that card's query, with no memory of this plan. For an "insight" card the request is the question itself, since no query is written for one.${correction ? `\n\n${correction}` : ''}`;
}

const SHAPE_RULES = `For a metric, return one row with one numeric value. For a line, area or bar chart, return a label or date column and one or more numeric columns that share a unit. For a scatter, return two numeric columns to plot against each other, one row per thing being compared, optionally with a category column to colour by. For a stacked chart, return exactly three columns in this order — a label or date column, a category column to stack by, and one numeric value column. For a heatmap, return exactly three columns: a category for one axis, a category for the other axis, and one numeric value for the cell. For a box plot, return a category column and a numeric value column, one row per observation: the spread within each category is what is plotted, so do not pre-aggregate. For a table, return clear named columns.

Unless the card is a "combo", do not combine measures of different units or magnitudes in one query, such as a percentage beside a count: the chart has a single y axis and the smaller series would be unreadable. For a "combo", return each measure as its own column. For a "pie", return one category column and one measure.`;

/**
 * Cards carry their own query when they are not reading a dataset, and the
 * dashboard period reaches them the same way.  Told to the generator here as
 * well as for datasets, because a card written on its own is the case where a
 * hardcoded month is easiest to reach for.
 */
function periodRule(sources?: ReadonlyArray<VerifiedSource>): string {
  const pruning = sources?.length
    ? `Filter on the period column named above where the listing gives one, since that is what lets BigQuery skip the periods nobody asked for.`
    : `Filter on the table's partitioning column where it has one, checking with the schema tools rather than assuming, since that is what lets BigQuery skip the periods nobody asked for.`;

  return `Bind the query to the dashboard period with the named parameters @start_date and @end_date: WHERE DATE(<date column>) >= @start_date AND DATE(<date column>) < @end_date. @end_date is already exclusive, so use < and not <=. ${pruning} Never write a date literal or a relative window such as CURRENT_DATE() - 90: the reader chooses the period, and a hardcoded one cannot be moved off.${sources?.length ? '' : ' When you validate the query, supply the dates in the tool call rather than substituting them into the answer.'} Do not DECLARE them as script variables and do not return more than one statement: every saved query is nested inside another one before it runs, and a script cannot be.`;
}

export function cardPrompt(card: {
  type: LensCardType;
  request: string;
  filterColumns?: Array<string>;
  /** Datasets already on this Lens, offered before a new query is written. */
  datasets?: Array<{ id: string; name: string; columns: Array<string> }>;
  /** Tables already looked up for this dashboard, so this turn needs no discovery of its own. */
  sources?: ReadonlyArray<VerifiedSource>;
  /** Set when a previous attempt returned something the dashboard cannot run. */
  correction?: string;
  /** The tier the deterministic guard expects, primed into the prompt. */
  tier?: TierProposal;
}): string {
  return `Create a single LE Lens ${card.type} card for this request: ${card.request}

${TIER_DECISION}

${tierPrimer(card.datasets ?? [], card.tier)}
${card.datasets?.length ? `\n${reuseRule(card.datasets)}\n` : ''}
${card.sources?.length ? schemaRule(card.sources) : 'Use the approved Luxury Escapes schema tools, write BigQuery SQL, and run it to validate the result.'} The final answer must include exactly one fenced sql block containing the production query, opening with these two comment lines and nothing else before the query:

  -- title: Your Card Name
  -- format: <one of the formats below>

The title names the card in at most six words, as a label rather than a sentence: "Average margin pct", not "I want to see the average margin". The format says how the card's own number should be read, so choose it from what the query computes rather than from what the request called it:

  number          a plain count or quantity
  number:2        a plain number shown to 2 decimal places
  currency:AUD    money, in that ISO currency
  percent:unit    a proportion still expressed from 0 to 1, such as 0.21
  percent:scaled  a percentage already multiplied by 100, such as 21.4

A ratio returned as 0.21 is percent:unit; printing it as a plain number leaves the reader to work out that it means 21%.

${SHAPE_RULES}

${periodRule(card.sources)}${card.filterColumns?.length ? `\n\n${filterRule(card.filterColumns)}` : ''}${card.correction ? `\n\n${card.correction}` : ''}

Reply with that one fenced sql block and nothing else. Prose around it is time the reader spends waiting.`;
}

/**
 * Reconciles a chat-produced replacement with the dataset the previous card
 * already reads. This is deliberately a separate turn: the chat answer was
 * made to answer the reader's question, while this turn protects the shared
 * dashboard model and every neighbouring card that depends on it.
 */
export function replacementDatasetPrompt(input: {
  type: LensCardType;
  title: string;
  candidateSql: string;
  dataset: { id: string; name: string; columns: Array<string>; sql: string };
  filterColumns?: Array<string>;
}): string {
  const { dataset } = input;
  return `A reader is replacing the “${input.title}” LE Lens ${input.type} card. The existing card reads the shared dataset below; preserve that shared model whenever it is honest and safe.

Dataset: ${dataset.id} — ${dataset.name}
Columns it currently returns: ${dataset.columns.join(', ')}
Existing shared dataset SQL:
\`\`\`sql
${dataset.sql}
\`\`\`

The chat has already produced this candidate answer SQL. It shows the reader's intended result, but it must not automatically detach the card from the shared data model:
\`\`\`sql
${input.candidateSql}
\`\`\`

Choose the first honest option:

1. If the existing dataset already answers it, reply with exactly:
  -- dataset: ${dataset.id}
  -- view: x=<column>; series=<column>; value=<column>; aggregate=<sum, avg, count, countDistinct, min, max or first>

2. If arithmetic over existing columns answers it, reply with exactly:
  -- dataset: ${dataset.id}
  -- field: <new_column_name> = <formula> [on totals|on each row]
  -- view: value=<new_column_name>

3. If the dataset has the right grain and rows but needs one or more extra columns, rewrite its full SQL in exactly one fenced sql block. It must retain every existing column with its exact name, add only what is needed, retain the dashboard date binding, and then reply:
  -- extend: ${dataset.id}
  -- view: x=<column>; series=<column>; value=<column>; aggregate=<sum, avg, count, countDistinct, min, max or first>
  -- grain: <the columns that make one row of the rewritten query unique, comma separated>

4. Only when sharing would change the grain, population, period, or meaning of other cards, reply exactly:
  -- cannot: <short reason>

Do not return a standalone card query. The application already has the candidate query as the safe fallback if this dataset cannot honestly support it.

${SHAPE_RULES}

${periodRule()}${input.filterColumns?.length ? `\n\n${filterRule(input.filterColumns)}` : ''}`;
}

export function modifyPrompt(card: {
  type: LensCardType;
  sql: string;
  change: string;
  filterColumns?: Array<string>;
  /** Set when a previous attempt returned something the dashboard cannot run. */
  correction?: string;
}): string {
  return `Modify this existing LE Lens ${card.type} card query.

Requested change: ${card.change}

Existing SQL:
\`\`\`sql
${card.sql}
\`\`\`

Use the approved Luxury Escapes schema tools as needed. Return exactly one fenced sql block containing the replacement BigQuery query, and run it to validate the result.

${SHAPE_RULES}

${periodRule()}${card.filterColumns?.length ? `\n\n${filterRule(card.filterColumns)}` : ''}${card.correction ? `\n\n${card.correction}` : ''}`;
}

/**
 * The one turn that goes looking, asked for the tables a brief needs and
 * nothing else.
 *
 * Separated from grouping because it depends only on the brief: it runs while
 * the plan is still being written, and the turns after it are given what it
 * found rather than searching again. Nothing here writes SQL, so a wrong guess
 * costs a listing rather than a query.
 */
export function sourcesPrompt(brief: string): string {
  return `Someone asked for this Lens: ${brief}

Find the warehouse tables that answer it, and reply with those tables and nothing else.

Rules:
- Use the approved Luxury Escapes schema tools. Look the terms up once, then inspect at most two tables — the two you already believe you will name.
- Name the fewest tables that cover the brief. One good table beats four possible ones: every table you name is one the turns after you have to read past.
- Fully qualify each table as project.dataset.table, exactly as the schema tools spell it.
- Give the column a dashboard would filter the period on, where the table has one.
- List the columns a dashboard built from this brief would read: the measures, the things it would split by, and the period column. Take them from what you inspected; do not go looking for more.
- Where the term lookup returned a certified metric definition, carry it forward under its table: the id, the aggregate expression exactly as certified, and any filter the definition rules out. The turns after you are not permitted to look it up again, so a definition you drop is one the dashboard invents for itself.
- Do not write SQL, do not run anything, and do not propose cards or datasets.

Reply with exactly one fenced json block and nothing after it:

{"sources":[{"table":"project.dataset.retail_sales","date":"order_date","columns":["order_date","store_name","brand","revenue_aud","cost_aud","order_id"],"metrics":[{"id":"commercial.example.br","expression":"SUM(revenue_aud)","avoid":["le_office_flag = FALSE"]}]}]}`;
}

/**
 * Cards planned separately usually come from the same few tables. Asking for
 * the data model before any SQL is written turns a dashboard of N queries into
 * one of a few, and cards that share a query can then filter each other.
 */
export function consolidatePrompt(
  cards: Array<{ title: string; type: string; request: string }>,
  options: { correction?: string; sources?: ReadonlyArray<VerifiedSource> } = {},
): string {
  const { correction, sources } = options;
  return `These cards were planned for one LE Lens dashboard:

${cards.map((card, index) => `${index + 1}. [${card.type}] ${card.title} — ${card.request}`).join('\n')}

Group them onto as few shared datasets as honestly possible. A dataset is one query several cards read; each card then takes its own cut of it.

Rules:
- Bind every card you can. A card left out becomes its own query: another scan of the same tables for a number the shared one already carries. Leave one out only where its grain genuinely differs.
- Group by GRAIN, not by table. Cards needing one row per day per store and cards needing one row per month per brand are different datasets even from the same table.
- A dataset must be able to answer every card assigned to it by grouping and summing its rows. If a card needs a column the dataset does not carry, either add the column or give the card its own dataset.
- Go to the FINEST grain the cards need, then roll up. A card wanting the total and a card wanting the total by hotel type are one dataset at "day and hotel type", not two: the total card sums every row. Never propose a dataset that is a roll-up of another dataset in the same reply, because summing the finer one produces it.
- A rate, share, average or "per" measure is NEVER a column the query returns. Store its parts as columns and declare a calculated field for it, so the same dataset answers it at every grain. A stored rate column cannot be rolled up, which is what forces a second dataset for the same numbers.
- Declare calculated fields in "fields" on the dataset. Each has a "name", an "expression" over that dataset's own columns, and a "when":
  - "when":"aggregate" is evaluated on each group's totals, so a column name inside it means that column summed. Every rate, share, average and per-unit measure is one of these: {"name":"margin_rate","expression":"gross_margin_aud / ttv_aud","when":"aggregate"} is total margin over total TTV, correct whether the card shows one number, a daily trend or a split by hotel type.
  - "when":"row" is evaluated on each returned row before grouping, and becomes an ordinary column a card can group, filter and sum by: {"name":"hotel_ttv","expression":"if(product = 'hotel', ttv_aud, 0)","when":"row"}.
  - Formulas take + - * / and parentheses, comparisons = <> > >= < <=, and/or/not, single-quoted text, and if(<test>, <then>, <otherwise>), coalesce(...), abs(...), round(<value>, <places>). Division by zero yields no value rather than an error.
  - Never write a rate as "when":"row": averaging per-row rates weights a $100 booking the same as a $30,000 one.
- A dataset is a summary, never an extract of raw rows. Choose the COARSEST grain that still answers every card assigned to it, and include every column those cards group or measure by.
- The grain must keep the result under a few thousand rows. If a grain would return more, coarsen it (a day rather than an hour, a brand rather than a product) or give a card its own dataset.
- Do not merge cards whose filters or time ranges differ.
- Aim for 1 to 3 datasets, and reach for 1 first. One is best when honest, because cards on the same dataset can filter each other.
- Name columns in snake_case, and declare exactly the columns the dataset returns.
- Use the SAME column name across datasets for the same thing. If two datasets both carry a vendor, both must call it "vendor": one name means one filter that moves every card, whereas "vendor" and "vendor_name" become two controls that look identical and disagree.
- By the same token, never reuse a name for a different thing.
- List in "dimensions" the columns a reader would filter by, such as a store, a brand or a market. Dimensions only: never a measure or total, and never a date or timestamp, which are filtered by range elsewhere.

${sources?.length ? schemaRule(sources) : 'Use the approved Luxury Escapes schema tools to confirm the columns exist.'} Reply with exactly one fenced json block and nothing after it:

{"datasets":[{"key":"retail_daily","name":"Retail sales by day and store","grain":"one row per day per store","columns":["day","store","brand","revenue_aud","orders","cost_aud"],"dimensions":["store","brand"],"fields":[{"name":"margin_rate","expression":"(revenue_aud - cost_aud) / revenue_aud","when":"aggregate"},{"name":"revenue_per_order","expression":"revenue_aud / orders","when":"aggregate"}],"request":"daily retail revenue, cost and order count by store and brand"}],
 "cards":[{"index":1,"datasetKey":"retail_daily","view":{"x":"day","series":"brand","value":"revenue_aud","aggregate":"sum"}},{"index":2,"datasetKey":"retail_daily","view":{"value":"margin_rate"}}],
${sources?.length ? '' : `\n "sources":[{"table":"project.dataset.retail_sales","date":"order_date","columns":["order_date","store_name","brand","revenue_aud","cost_aud","order_id"]}]`}}
${sources?.length ? 'Nobody writes the SQL in this reply: each dataset is written by a separate turn that is given the same table listing you were given, so a dataset must be answerable from the tables above.' : 'In "sources", list every warehouse table you looked up, fully qualified, with the column you would filter the period on and the columns of it that these datasets read. Nobody writes the SQL in this reply: each dataset is written by a separate turn that is given this listing instead of looking the tables up again, so a table you leave out, or a column you name loosely, is a turn that has to go and find it.'}

For each card use its number above as "index". "view" describes how that card reduces the dataset: "x" to group by, "series" to split by, "value" to measure, "aggregate" one of sum, avg, count, countDistinct, min, max, first, or the name of a calculated field for a rate, share or average. A card reading an aggregate field takes no "aggregate" of its own: the field says how it is computed. A metric card takes no "x" or "series". Omit a card entirely if it cannot be answered from any dataset you propose; it will keep its own query.${correction ? `\n\n${correction}` : ''}`;
}

/** Datasets are generated against the column contract the plan declared. */
/**
 * Offers the datasets already on the Lens before any new SQL is written.
 *
 * A card that could be a cut of a dataset beside it should be one: the runner
 * runs each distinct query once, so a reused dataset costs nothing to add, and
 * two cards reading the same rows cannot disagree the way two queries can.
 */
function reuseRule(datasets: Array<{ id: string; name: string; columns: Array<string> }>): string {
  const listed = datasets
    .map((dataset) => `  ${dataset.id} — ${dataset.name}\n    columns: ${dataset.columns.join(', ')}`)
    .join('\n');

  return `This Lens already has these shared datasets:

${listed}

Before writing any SQL, work down these four in order and take the first that honestly answers the request. Each is cheaper for the reader than the one after it: the first three add no query to the dashboard at all, and the runner runs each distinct query once, so a card on an existing dataset costs nothing to add and cannot disagree with the cards beside it.

1. THE DATASET AS IT STANDS (tier 1, a view change). The request is a cut of rows a dataset already returns. Answer with these lines and nothing else:

  -- tier: view
  -- tierReason: <one sentence>
  -- dataset: <the id above>
  -- view: x=<column to group by>; series=<column to split by>; value=<column to measure>; aggregate=<sum, avg, count, countDistinct, min, max or first>

2. A CALCULATED FIELD ON TOP OF IT (tier 2). Every column the request needs is there, but the number itself is arithmetic over them: a rate, a share, an average, a per-unit value, a difference, or a subtotal under a condition. Nothing needs to be read from the warehouse again. Answer with these lines:

  -- tier: field
  -- tierReason: <one sentence>
  -- dataset: <the id above>
  -- field: <new_column_name> = <formula> [on totals|on each row]
  -- view: value=<new_column_name>

  "on totals" is evaluated once per group, so a column name inside the formula means that column's total for the group. EVERY rate, share, average and per-unit measure is "on totals": margin_rate = gross_margin_aud / ttv_aud on totals is total margin over total TTV, which is right whether the card shows one number, a daily trend or a split by category. "on each row" is evaluated per returned row before grouping and becomes an ordinary column the card can group, filter and sum by, for example hotel_ttv = if(product = 'hotel', ttv_aud, 0) on each row.
  Never write a rate "on each row": averaging per-row rates weights a $100 booking the same as a $30,000 one.
  A rate or share field is the plain quotient, from 0 to 1, never multiplied by 100: the card prints it as a percentage.
  Formulas take + - * / and parentheses, comparisons = <> > >= < <=, and/or/not, single-quoted text, and if(<test>, <then>, <otherwise>), coalesce(...), abs(...), round(<value>, <places>). Dividing by zero gives no value rather than an error. Name columns exactly as spelled above.

3. THE DATASET, EXTENDED (tier 3, since it reads the warehouse again). The grain is right and the dataset is the right rows, but a column the request needs is genuinely not being returned, and no arithmetic over the existing columns produces it. Answer with the dataset's whole query rewritten to return its existing columns unchanged PLUS the new ones, in one \`\`\`sql block, then the binding lines:

  -- extend: <the id above>
  -- view: value=<new column>
  -- grain: <the columns that make one row of the rewritten query unique, comma separated>

  The rewritten query MUST still return every column listed above under that id, spelled the same way, and must keep its own date predicate if it has one. Other cards read those columns and would break. State the grain even when it has not changed: it is checked against the rewritten rows before anything is applied.

4. A NEW DATASET (tier 3). The request needs different rows: a different grain, a different table, a different period or a different filter, and nothing above answers it. This becomes a dataset the board can share, not a query only this card can see, so keep it at the coarsest grain that still answers the request. Write SQL as normal, opening the answer with:

  -- tier: query
  -- tierReason: <one sentence naming the column or grain no dataset above carries>
  -- grain: <the columns that make one row of this query unique, comma separated>

A coarser period is never a reason to leave rung 1: a card can group a daily dataset by week(<date column>), month(<date column>) or quarter(<date column>), so a daily dataset answers a monthly card.

Omit any of x, series or value that the card does not need; a metric card has no x and no series.

`;
}

/**
 * Changes a card that reads a shared dataset.
 *
 * Such a card has no SQL to modify: what it shows is which columns it groups
 * and measures. Asking for a query back would either fail or quietly detach the
 * card from the dataset the rest of the board shares.
 */
export function viewChangePrompt(card: {
  type: LensCardType;
  datasetId: string;
  datasetName: string;
  columns: Array<string>;
  current: string;
  change: string;
}): string {
  return `An LE Lens ${card.type} card reads a shared dataset. Change what it shows.

Dataset: ${card.datasetId} — ${card.datasetName}
Columns it returns: ${card.columns.join(', ')}
The card currently shows: ${card.current}

Requested change: ${card.change}

Do not write SQL. The dataset's query is shared with other cards and is not being changed. Answer with these two lines and nothing else:

  -- dataset: ${card.datasetId}
  -- view: x=<column to group by>; series=<column to split by>; value=<column to measure>; aggregate=<sum, avg, count, countDistinct, min, max or first>

Omit any of x, series or value the card does not need; a metric card has no x and no series. Every column must be one of the columns listed above, exactly as spelled. A coarser period is available without a new column: x may be hour(<DATETIME or TIMESTAMP column>), week(<date column>), month(<date column>) or quarter(<date column>).

If the dataset does not carry what the change needs, do not invent a column. Answer with exactly one line instead, naming what is missing:

  -- cannot: <what the dataset does not carry>`;
}

export function datasetPrompt(dataset: {
  name: string;
  grain: string;
  columns: Array<string>;
  request: string;
  /** Columns every dashboard filter binds to, which this query has to expose. */
  filterColumns?: Array<string>;
  /** Tables already looked up for this dashboard, so this turn needs no discovery of its own. */
  sources?: ReadonlyArray<VerifiedSource>;
  /** Set when a previous attempt returned something other than the dataset. */
  correction?: string;
}): string {
  return `Write one BigQuery query for an LE Lens dataset.

Name: ${dataset.name}
Grain: ${dataset.grain}
It must answer: ${dataset.request}

Return exactly these columns, with exactly these names and in this order: ${dataset.columns.join(', ')}${
    dataset.filterColumns?.length ? `\n\n${filterRule(dataset.filterColumns)}` : ''
  }

Several cards read this one query and each groups it further, so aggregate to the grain above and no finer: GROUP BY every non-numeric column listed, and aggregate every numeric one. Do not select raw rows.

Bound it in time with the dashboard's period, which arrives as the named parameters @start_date and @end_date:

  WHERE DATE(<date column>) >= @start_date AND DATE(<date column>) < @end_date

@end_date is already exclusive, so use < and not <=. Never write a date literal or a relative window such as CURRENT_DATE() - 90: the reader chooses the period, and a hardcoded one cannot be moved off.

${
  dataset.sources?.length
    ? 'Filter on the period column named in the listing above where it gives one.'
    : "Filter on the table's partitioning column where it has one, and check which column that is with the schema tools rather than assuming."
} A predicate on the partitioning column lets BigQuery skip the partitions outside the period; the same predicate on any other date reads the whole table first, which is the difference between a dashboard that is cheap to move around and one that is not. Where the table is not partitioned, pick the date the question is actually about.

Say which date you used in two comment lines at the top of the query itself, inside the fenced block, along with any other date columns the result exposes that a reader might want the period to follow instead:

  -- date: <the column the period filters on>
  -- dates: <every date column in the result, comma separated>

The result must be a summary of at most a few thousand rows. A dataset that returns a large extract is unusable: the rows are held in the browser and filtered there.

${
  dataset.sources?.length
    ? schemaRule(dataset.sources)
    : 'Use the approved Luxury Escapes schema tools. Check the query by running a bounded probe through the tool \u2014 wrap it and select COUNT(*), or add a LIMIT \u2014 and never run it in a way that returns the whole result, which is large enough to break this conversation.\n\nWhen you probe, supply the period yourself \u2014 set the parameters in the tool call, or substitute literal dates into the probe only. The production query keeps @start_date and @end_date.'
}

Do not use DECLARE, and do not return more than one statement. Several cards read this query by nesting it inside another one, so a multi-statement script cannot be used at all, however well it runs on its own.

End your answer with exactly one fenced sql block, and let that block be the production query: the two comment lines above, then a query selecting ${dataset.columns.join(', ')} and nothing else. Do not fence the probe. A block selecting COUNT(*) is the check rather than the dataset and would be rejected. Do not add a title comment; the two date lines are the only comments needed.${dataset.correction ? `\n\n${dataset.correction}` : ''}`;
}

/**
 * The one turn that asks why finished queries answer nothing.
 *
 * Every query here already compiles and already returns no rows, so there is
 * nothing to check and everything to look up: what a column really holds is a
 * fact about the warehouse, not about the SQL. Asked for the whole board at
 * once because the cause is usually shared — one vocabulary mistake spread
 * across every query that filtered on it — and because a board of empty cards
 * should cost one turn to diagnose rather than one turn each.
 */
export function emptyResultPrompt(queries: ReadonlyArray<{ id: string; title: string; sql: string }>): string {
  return `${
    queries.length === 1 ? 'This Lens query runs' : `These ${queries.length} Lens queries run`
  } without error and ${queries.length === 1 ? 'returns' : 'return'} no rows at all over the last 90 days.

The syntax is fine, so something in ${queries.length === 1 ? 'it' : 'them'} matches no data. In order of likelihood: a value literal the column never holds, such as \`category = 'Hotel'\` where every row says \`hotel\`; a join key that never matches; a date column that is null over the period; a combination of predicates that is empty by construction.

${queries
  .map(
    (query) => `Query ${query.id} — ${query.title}:
\`\`\`sql
${query.sql}
\`\`\``,
  )
  .join('\n\n')}

For each one, find out what the columns it filters on actually contain — run your own bounded probe with the schema tools, such as SELECT DISTINCT on the filtered column with a LIMIT, or COUNT(*) with and without the predicate. Supply the period yourself in any probe; the production query keeps @start_date and @end_date.

Then, only where a predicate is genuinely wrong, return the corrected query. Change nothing else: the same output columns with the same names in the same order, the same grain, the same date bounds, the same comment lines at the top. A rewrite that renames or drops a column breaks the cards reading it and will be discarded.

Where a query is correctly written and the period simply holds no such rows, leave it alone and say so in prose. An empty answer that is true is not a defect.

Return one fenced sql block per query you corrected and none for the others, each beginning with its id so it can be matched back:

  -- query: <id>

Do not use DECLARE and return no more than one statement per block.`;
}
